// ================================
// IMPORTS
// ================================
import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"

// ================================
// TYPES
// ================================

/**
 * Payload pour l'envoi d'un feedback utilisateur
 */
export type FeedbackData = {
    /** Sujet du feedback */
    subject: string
    /** Description détaillée */
    description: {
        text: string
    }
    /** Tags optionnels pour catégorisation */
    tags?: Record<string, string>
}

/**
 * Payload pour le signalement d'un bug ou problème technique
 */
export type BugIssueData = {
    /** Sujet du rapport */
    subject: string
    /** Description détaillée avec métadonnées */
    description: {
        /** Niveau de gravité (low, medium, high) - optionnel pour les bugs */
        level?: string
        /** Texte descriptif */
        text: string
        /** URL ou chemin de la page courante */
        currentPage: string
        /** Identifiant ou email de l'utilisateur */
        username: string
        /** Sujet du problème technique connu - optionnel */
        issueSubject?: string
        /** Propriétés additionnelles (ex: contexte technique) */
        [key: string]: unknown
    }
    /** Tags optionnels */
    tags?: Record<string, string>
}

// ================================
// SERVICE
// ================================

/**
 * Service permettant d'envoyer des feedbacks et de signaler des bugs ou problèmes techniques au support.
 * Utilise l'API POST /api/metier/v5/support.
 */
export class SupportService {
    /**
     * Envoie un feedback utilisateur à l'équipe support.
     * POST /api/metier/v5/support avec type "Feedback"
     * @param data - Données du feedback (sujet, description)
     * @returns Promise résolue en cas de succès
     */
    async sendFeedback(data: FeedbackData): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.post("/api/metier/v5/support", {
                type: "Feedback",
                subject: data.subject,
                description: data.description,
                tags: data.tags,
            })
        })
    }

    /**
     * Signale un bug ou un problème technique à l'équipe support.
     * POST /api/metier/v5/support avec type "Bug" ou "Support"
     * @param data - Données du rapport (sujet, description avec level, currentPage, username, etc.)
     * @returns Promise résolue en cas de succès
     */
    async reportBugIssue(data: BugIssueData): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.post("/api/metier/v5/support", {
                type: data.description.issueSubject ? "Support" : "Bug",
                subject: data.subject,
                description: data.description,
                tags: data.tags,
            })
        })
    }
}
